import { MantineProvider } from '@mantine/core';
import { SortDir } from '@shared/constants';
import { GlobalMessages } from '@shared/localization/messages';
import dayjs from 'dayjs';
import type { DataTableSortStatus } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTenantQueryActions } from '@/api/hooks/tenantQueryActions';
import IconFilter from '@/assets/Icons/IconFilter';
import TableSkeleton from '@/components/atoms/TableSkeleton';
import Search from '@/components/molecules/Search';
import { TableColumnsSelector } from '@/components/organism/DataTable/components/columnsSelector';
import { ExportButton } from '@/components/organism/DataTable/components/exportButton';
import { FilterOverlay } from '@/components/organism/DataTable/components/FilterOverlay';
import { useExport } from '@/components/organism/DataTable/hooks/exports/useExport';
import type {
  eExportFormat,
  ExportableDataTableColumn,
  IDataTableProps,
} from '@/components/organism/DataTable/model';
import { defaultDataTableProps } from '@/components/organism/DataTable/model';
import { noop } from '@/helpers/noop';
import { useMessages } from '@/hooks/messages';
import { useHorizontalOverflow } from '@/hooks/useHorizontalOverflow';
import { usePdfHeaderFooterDataGetter } from '@/hooks/usePdfHeaderFooterDataGetter';

import { ExportButtonsSelector } from './components/exportSelector';
import type { ExportColumn, ExportRow, ExportTable } from './hooks/exports/model';

export const DataTableComponent = <T extends object>(props: IDataTableProps<T>) => {
  const {
    data,
    getExportData,
    exportTitle,
    pdfHeaderFooterDataPromise,
    showColumnSelector,
    showEstimationSelects,
    hidePagination,
    searchFunction,
    exportConfiguration,
    exportsAsDropdown,
    onPageChange = noop,
    columnsConfiguration,
    onSortStatusChange = noop,
    pagination: { totalItems, recordsPerPage = 15 },
    fetchingData,
    rowStyle,
    className,
  } = props ?? defaultDataTableProps;

  const globalMessages = useMessages(GlobalMessages);
  const [isFilterOverlayOpen, setIsFilterOverlayOpen] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleSearch = useCallback(
    (value: string) => {
      if (searchFunction) {
        searchFunction(value);
      }
    },
    [searchFunction],
  );

  const [columnsState, setColumnsState] =
    useState<ExportableDataTableColumn<T>[]>(columnsConfiguration);

  const hasHorizontalScroll = useHorizontalOverflow(tableContainerRef, [
    data,
    columnsState,
    fetchingData,
  ]);

  // Use first sortable column as default sort status
  const defaultSortStatus = useMemo<DataTableSortStatus<T>>(() => {
    const firstSortableColumn = columnsConfiguration.find((col) => col.sortable);
    return {
      columnAccessor: (firstSortableColumn?.accessor ??
        columnsConfiguration[0]?.accessor ??
        '') as keyof T,
      direction: SortDir.ASC,
    };
  }, [columnsConfiguration]);

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<T>>(defaultSortStatus);

  const { genericPdfHeader } = usePdfHeaderFooterDataGetter(exportTitle ?? '');
  const { useGetTenant } = useTenantQueryActions();
  const { isLoading: isLoadingTenant, data: tenantData } = useGetTenant();

  useEffect(() => {
    // leave only enabled columns. Functional update reads the previous columns
    // state from the updater so columnsState is not a dependency (avoids the
    // re-run loop that adding it would cause).
    setColumnsState((prevColumnsState) =>
      columnsConfiguration.filter(
        (columnConfig) =>
          !!prevColumnsState.find(
            (enabledColumn) => columnConfig.accessor === enabledColumn.accessor,
          ),
      ),
    );
  }, [columnsConfiguration, setColumnsState]);

  const exporter = useExport({
    fileName: [tenantData?.name, exportTitle, dayjs().format('YYYYMMDDhhmm')]
      .filter((x) => !!x?.trim())
      .join('_'),
    orientation: 'portrait',
  });

  const handleExportButtonClick = useCallback(
    async (format: eExportFormat) => {
      const tables: ExportTable[] = [
        {
          title: undefined,
          columns: columnsState
            .filter((x) => !x.excludeFromExport)
            .map<ExportColumn>((x) => ({
              accessor: x.accessor as string,
              headerLabel: x.title as string,
              isHtml: x.isHtml,
            })),
          rows: getExportData
            ? (await getExportData()).map((row) =>
                Object.keys(row)
                  .filter((key) =>
                    columnsState.some((col) => col.accessor === key && !col.excludeFromExport),
                  )
                  .reduce((acc, key) => {
                    acc[key] = row[key];
                    return acc;
                  }, {} as ExportRow),
              )
            : ((data as ExportRow[]) /* TODO: fix typings */ ?? []),
        },
      ];
      exporter(
        format,
        tables,
        pdfHeaderFooterDataPromise ?? Promise.resolve({ header: genericPdfHeader }),
      );
    },
    [columnsState, data, exporter, genericPdfHeader, getExportData, pdfHeaderFooterDataPromise],
  );

  const [page, setPage] = useState(1);

  const buttonExportCollection = useCallback(() => {
    if (!exportConfiguration || exportConfiguration.length === 0) return null;
    if (exportsAsDropdown) {
      return (
        <ExportButtonsSelector
          exportConfiguration={exportConfiguration}
          onExportButtonClick={async (format) => {
            await handleExportButtonClick(format);
          }}
        />
      );
    }
    return (
      exportConfiguration?.map((format) => {
        return (
          <ExportButton
            format={format}
            onClick={async () => {
              await handleExportButtonClick(format);
            }}
            key={format}
          />
        );
      }) ?? null
    );
  }, [exportConfiguration, exportsAsDropdown, handleExportButtonClick]);

  if (isLoadingTenant) {
    return <TableSkeleton />;
  }

  return (
    <MantineProvider>
      <div className='mb-5 flex flex-col gap-3'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <div className='flex'>{props.toolbarContent}</div>

          <div className='flex flex-wrap items-center gap-3'>
            {searchFunction && <Search handleOnChange={handleSearch} />}

            {showEstimationSelects && (
              <div className='hidden lg:flex lg:items-center'>{props.moreSelects}</div>
            )}
            {showEstimationSelects && (
              <button
                className='dark:bg-c16 flex h-12 w-14 items-center justify-center rounded-md border bg-white p-4 lg:hidden dark:border-white'
                onClick={() => setIsFilterOverlayOpen(true)}
              >
                <IconFilter className='h-6 w-6 text-black dark:text-white' />
              </button>
            )}

            <div className='flex h-14 items-center gap-3'>
              {buttonExportCollection()}
              {showColumnSelector && (
                <div className='flex h-14 items-center'>
                  <TableColumnsSelector<T>
                    columns={columnsConfiguration}
                    onColumnsChange={setColumnsState}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Overlay for mobile & iPad */}
      <FilterOverlay isOpen={isFilterOverlayOpen} onClose={() => setIsFilterOverlayOpen(false)}>
        {props.moreSelects}
      </FilterOverlay>

      <div className='relative'>
        {hasHorizontalScroll && (
          <div className='pointer-events-none absolute top-0 right-0 z-[5] block h-full w-8 rounded-r-xl bg-gradient-to-l from-white/80 to-transparent dark:from-black/80' />
        )}
        <div ref={tableContainerRef}>
          <DataTable<T>
            highlightOnHover
            withTableBorder
            className={`table-hover cursor-pointer rounded-xl whitespace-nowrap ${className}`}
            fetching={fetchingData}
            records={data}
            columns={columnsState}
            totalRecords={totalItems}
            recordsPerPage={recordsPerPage}
            // mantine-datatable shows the pagination component whenever `page` is provided, so
            // hiding it means passing undefined through a prop typed as number.
            page={hidePagination !== true ? page : (undefined as unknown as number)}
            onPageChange={(currentPage) => {
              setPage(currentPage);
              onPageChange(currentPage);
            }}
            paginationText={() => ''}
            loadingText={globalMessages.loading()}
            noRecordsText={globalMessages.noDataFound()}
            sortStatus={sortStatus}
            onSortStatusChange={(status: DataTableSortStatus<T>) => {
              setSortStatus(status);
              onSortStatusChange(status);
            }}
            rowStyle={rowStyle}
          />
        </div>
      </div>
    </MantineProvider>
  );
};
